import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

export interface ScannerRuntimeContent {
  readonly title: string;
  readonly description: string;
  readonly refresh: string;
  readonly loading: string;
  readonly loadErrorTitle: string;
  readonly loadErrorDescription: string;
  readonly ready: string;
  readonly unavailable: string;
  readonly modeLabel: string;
  readonly mode: {
    readonly oidc: string;
    readonly legacyRunner: string;
    readonly unavailable: string;
  };
  readonly legacyRunnerLabel: string;
  readonly centralScanLabel: string;
  readonly centralReady: string;
  readonly centralUnavailable: string;
  readonly centralNotChecked: string;
  readonly centralReadyDescription: string;
  readonly centralMissingDescription: string;
  readonly adminServiceLabel: string;
  readonly packRegistryLabel: string;
  readonly configured: string;
  readonly missing: string;
  readonly legacyNotRequired: string;
  readonly legacyReady: string;
  readonly legacyNotConfigured: string;
  readonly legacyUnreachable: string;
  readonly oidcReadyTitle: string;
  readonly oidcReadyDescription: string;
  readonly policyDisabled: string;
  readonly manualScan: {
    readonly checking: string;
    readonly unavailable: string;
    readonly oidcManaged: string;
    readonly runnerUnconfigured: string;
    readonly runnerUnavailable: string;
  };
  readonly action: {
    readonly none: string;
    readonly configureRegistry: string;
    readonly configureRunner: string;
    readonly checkRunner: string;
    readonly checkRegistry: string;
  };
}

const SCANNER_RUNTIME_CONTENT: Bilingual<ScannerRuntimeContent> = {
  'pt-BR': {
    title: 'Disponibilidade do scanner',
    description: 'Modo efetivo e prontidão da infraestrutura, sem exibir valores sensíveis.',
    refresh: 'Verificar novamente',
    loading: 'Verificando a infraestrutura do scanner…',
    loadErrorTitle: 'Não foi possível verificar o scanner',
    loadErrorDescription: 'Tente novamente ou verifique a conexão do Admin com o registry.',
    ready: 'Disponível',
    unavailable: 'Indisponível',
    modeLabel: 'Modo efetivo',
    mode: {
      oidc: 'Scan central OIDC',
      legacyRunner: 'Runner legado',
      unavailable: 'Não configurado',
    },
    legacyRunnerLabel: 'Runner legado',
    centralScanLabel: 'Scan central multiprovedor',
    centralReady: 'Disponível',
    centralUnavailable: 'Configuração incompleta',
    centralNotChecked: 'Não verificado',
    centralReadyDescription:
      'GitHub Actions, GitLab CI e Bitbucket Pipelines usam o mesmo scan central atestado.',
    centralMissingDescription: 'Configurações ausentes no pack-registry:',
    adminServiceLabel: 'admin-service',
    packRegistryLabel: 'pack-registry',
    configured: 'Configurada',
    missing: 'Ausente',
    legacyNotRequired: 'SCAN_RUNNER_URL não é necessário; sua ausência é esperada com OIDC.',
    legacyReady: 'Configurado e acessível',
    legacyNotConfigured: 'Configuração incompleta',
    legacyUnreachable: 'Configurado, mas sem resposta',
    oidcReadyTitle: 'Scan OIDC ativo',
    oidcReadyDescription:
      'O workflow central imutável da Planuze (H4 → H3) escaneia o artefato recebido, produz e envia o atestado. O caller do publisher apenas materializa a chave, executa o release, agenda, acompanha e finaliza.',
    policyDisabled: 'A política do scanner está desligada no config-service.',
    manualScan: {
      checking: 'Verificando disponibilidade do scanner…',
      unavailable: 'Não foi possível validar a infraestrutura. Consulte Config de moderação.',
      oidcManaged:
        'O modo OIDC recebe scans pelo workflow central imutável da Planuze (H4 → H3); o re-scan manual legado não se aplica.',
      runnerUnconfigured:
        'O runner legado não está configurado nos serviços. Consulte Config de moderação.',
      runnerUnavailable: 'O runner legado está configurado, mas não respondeu ao health check.',
    },
    action: {
      none: 'Nenhuma ação necessária.',
      configureRegistry: 'Configure PACK_REGISTRY_URL e INTERNAL_SHARED_SECRET no admin-service.',
      configureRunner:
        'Sem OIDC, configure SCAN_RUNNER_URL no admin-service e no pack-registry, além de REGISTRY_INTERNAL_URL no registry.',
      checkRunner:
        'O runner está configurado, mas o health check falhou. Verifique o serviço e a rede interna.',
      checkRegistry:
        'O Admin não conseguiu validar o scan central do pack-registry. Verifique o serviço, o D1 e as variáveis PACK_SCAN_*.',
    },
  },
  'en-US': {
    title: 'Scanner availability',
    description: 'Effective mode and infrastructure readiness without exposing sensitive values.',
    refresh: 'Check again',
    loading: 'Checking scanner infrastructure…',
    loadErrorTitle: 'Could not check the scanner',
    loadErrorDescription: 'Try again or check the Admin connection to the registry.',
    ready: 'Available',
    unavailable: 'Unavailable',
    modeLabel: 'Effective mode',
    mode: {
      oidc: 'Central OIDC scan',
      legacyRunner: 'Legacy runner',
      unavailable: 'Not configured',
    },
    legacyRunnerLabel: 'Legacy runner',
    centralScanLabel: 'Multi-provider central scan',
    centralReady: 'Available',
    centralUnavailable: 'Incomplete configuration',
    centralNotChecked: 'Not checked',
    centralReadyDescription:
      'GitHub Actions, GitLab CI, and Bitbucket Pipelines use the same attested central scan.',
    centralMissingDescription: 'Missing pack-registry configuration:',
    adminServiceLabel: 'admin-service',
    packRegistryLabel: 'pack-registry',
    configured: 'Configured',
    missing: 'Missing',
    legacyNotRequired: 'SCAN_RUNNER_URL is not required; its absence is expected with OIDC.',
    legacyReady: 'Configured and reachable',
    legacyNotConfigured: 'Incomplete configuration',
    legacyUnreachable: 'Configured but not responding',
    oidcReadyTitle: 'OIDC scanning active',
    oidcReadyDescription:
      'The immutable Planuze central workflow (H4 → H3) scans the received artifact, produces, and submits the attestation. The publisher caller only materializes the key, runs the release, schedules, polls, and finalizes.',
    policyDisabled: 'The scanner policy is disabled in config-service.',
    manualScan: {
      checking: 'Checking scanner availability…',
      unavailable: 'The infrastructure could not be validated. See Moderation config.',
      oidcManaged:
        'OIDC mode receives scans from the immutable Planuze central workflow (H4 → H3); legacy manual re-scan does not apply.',
      runnerUnconfigured:
        'The legacy runner is not configured in the services. See Moderation config.',
      runnerUnavailable: 'The legacy runner is configured but did not answer the health check.',
    },
    action: {
      none: 'No action required.',
      configureRegistry: 'Configure PACK_REGISTRY_URL and INTERNAL_SHARED_SECRET in admin-service.',
      configureRunner:
        'Without OIDC, configure SCAN_RUNNER_URL in admin-service and pack-registry, plus REGISTRY_INTERNAL_URL in the registry.',
      checkRunner:
        'The runner is configured, but its health check failed. Check the service and internal network.',
      checkRegistry:
        'Admin could not validate the pack-registry central scan. Check the service, D1, and PACK_SCAN_* variables.',
    },
  },
};

export const useScannerRuntimeContent = (): ScannerRuntimeContent =>
  useBilingual(SCANNER_RUNTIME_CONTENT);
